import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const FeedbackModal = ({ isOpen, onClose }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!message.trim() || message.trim().length < 5) {
            toast.error('Please enter your feedback (at least 5 characters)');
            return;
        }

        setIsLoading(true);

        try {
            await axios.post(`${BACKEND_URL}/api/feedback`, {
                name: name.trim() || undefined,
                email: email.trim() || undefined,
                message: message.trim()
            });
            
            toast.success('Thank you for your feedback!');
            onClose();
            resetForm();
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Failed to submit feedback. Please try again.';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setName('');
        setEmail('');
        setMessage('');
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="glass-card w-full max-w-md overflow-hidden relative z-10"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="feedback-modal"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-5 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-accent/20">
                                <MessageSquare className="w-5 h-5 text-accent" />
                            </div>
                            <div>
                                <h3 className="font-heading font-semibold text-lg">Send Feedback</h3>
                                <p className="text-xs text-gray-500">Help us improve Vibe Coding Coach</p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="hover:bg-white/10"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-5 space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm text-gray-400">Name (optional)</label>
                            <Input
                                data-testid="feedback-name-input"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name"
                                className="bg-surface border-white/10"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm text-gray-400">Email (optional)</label>
                            <Input
                                data-testid="feedback-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="bg-surface border-white/10"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm text-gray-400">
                                Your Feedback <span className="text-destructive">*</span>
                            </label>
                            <textarea
                                data-testid="feedback-message-input"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Share your thoughts, report bugs, or suggest features..."
                                required
                                rows={4}
                                className="w-full bg-surface border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-gray-600 focus:border-accent/50 focus:outline-none resize-none"
                            />
                        </div>

                        <Button
                            data-testid="feedback-submit-btn"
                            type="submit"
                            disabled={isLoading || !message.trim()}
                            className="w-full btn-glow bg-accent hover:bg-accent/90"
                        >
                            {isLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Send Feedback
                                </>
                            )}
                        </Button>
                    </form>

                    {/* Footer */}
                    <div className="px-5 pb-5">
                        <p className="text-xs text-gray-600 text-center">
                            Your feedback helps us build a better tool for everyone.
                        </p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};
